import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowClockwise, MagnifyingGlassPlus, MagnifyingGlassMinus } from "@phosphor-icons/react";

const SIZE = 320;
const ACCEPT = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/** Preview → drag to reposition → zoom → rotate → confirm. Uploads a 512×512 JPEG. */
export const PhotoUploader = ({ open, onOpenChange, studentId, hasCurrent, onDone }) => {
  const [file, setFile] = useState(null);
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const canvas = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (!file) { setImg(null); return; }
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => { setImg(i); setZoom(1); setRot(0); setPos({ x: 0, y: 0 }); };
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, c.width, c.height);
    const scale = Math.max(c.width / img.width, c.height / img.height) * zoom;
    ctx.save();
    ctx.translate(c.width / 2 + pos.x * (c.width / SIZE), c.height / 2 + pos.y * (c.height / SIZE));
    ctx.rotate((rot * Math.PI) / 180);
    ctx.drawImage(img, (-img.width * scale) / 2, (-img.height * scale) / 2, img.width * scale, img.height * scale);
    ctx.restore();
  }, [img, zoom, rot, pos]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPT.includes(f.type)) return toast.error("Please choose a JPG, PNG, GIF or WEBP image");
    if (f.size > 5 * 1024 * 1024) return toast.error("Photos must be smaller than 5 MB");
    setFile(f);
  };

  const onDown = (e) => { drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; };
  const onMove = (e) => { if (drag.current) setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); };
  const onUp = () => { drag.current = null; };

  const upload = async () => {
    if (!img) return toast.error("Choose a photo first");
    if (hasCurrent && !window.confirm("Replace the current profile photo?")) return;
    setBusy(true);
    try {
      const blob = await new Promise((res) => canvas.current.toBlob(res, "image/jpeg", 0.9));
      const fd = new FormData();
      fd.append("file", new File([blob], "profile.jpg", { type: "image/jpeg" }));
      const { data } = await api.post(`/students/${studentId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.pending ? "Photo submitted — an administrator will review it" : "Profile photo updated");
      setFile(null);
      onOpenChange(false);
      onDone?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setFile(null); onOpenChange(o); }}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="photo-uploader">
        <DialogHeader>
          <DialogTitle className="font-display">Profile photograph</DialogTitle>
          <DialogDescription>JPG, PNG, GIF or WEBP up to 5 MB. Drag to reposition, zoom and rotate, then save.</DialogDescription>
        </DialogHeader>
        <input type="file" accept={ACCEPT.join(",")} onChange={pick} className="text-sm" data-testid="photo-file-input" />
        {img && (
          <>
            <canvas ref={canvas} width={512} height={512}
                    className="mx-auto rounded-2xl cursor-move touch-none border border-border"
                    style={{ width: SIZE, height: SIZE }}
                    onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
                    data-testid="photo-canvas" />
            <div className="flex justify-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setZoom((z) => Math.max(1, z - 0.15))}
                      data-testid="photo-zoom-out"><MagnifyingGlassMinus size={16} /></Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setZoom((z) => Math.min(4, z + 0.15))}
                      data-testid="photo-zoom-in"><MagnifyingGlassPlus size={16} /></Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRot((r) => (r + 90) % 360)}
                      data-testid="photo-rotate"><ArrowClockwise size={16} className="mr-1" /> Rotate</Button>
            </div>
          </>
        )}
        <Button onClick={upload} disabled={busy || !img}
                className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="photo-save-btn">
          {busy ? "Uploading…" : hasCurrent ? "Replace photo" : "Save photo"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
